import request from "umi-request";
import { environment } from "./../../environment/environment";
import type { ApiResponse } from "../model/apiResponse";
import type { IAddress } from "../model/address";

export type MapProvider = "google" | "digimaps";

export type MapRuntimeConfig = {
  configuredProvider: MapProvider;
  provider: MapProvider;
  supportsTiles: boolean;
  hasGoogleApiKey: boolean;
  hasDigiMapsApiKey: boolean;
};

export type MapSuggestion = {
  id: string;
  label: string;
  provider: MapProvider;
  requiresResolve: boolean;
  latitude?: number;
  longitude?: number;
};

export type ResolvedMapAddress = {
  label: string;
  provider: MapProvider;
  latitude: number;
  longitude: number;
};

export type ResolveMapAddressRequest = {
  id: string;
  provider: MapProvider;
  label?: string;
  sessionToken?: string;
};

type LegacyDigiMapFeature = {
  geometry?: {
    coordinates?: number[];
  };
  properties?: {
    id?: string;
    label?: string;
    name?: string;
  };
};

function normalizeApiError<T>(errorResponse: any): ApiResponse<T> {
  if (errorResponse?.data) {
    return errorResponse.data as ApiResponse<T>;
  }

  return {
    resultCode: false,
    data: undefined as T,
    message: errorResponse?.message ?? "Request failed",
    exception: errorResponse,
  };
}

function mapLegacySuggestion(
  feature: LegacyDigiMapFeature,
): MapSuggestion | null {
  const coordinates = feature?.geometry?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return null;
  }

  const longitude = Number(coordinates[0]);
  const latitude = Number(coordinates[1]);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  const label =
    feature?.properties?.label?.trim() ||
    feature?.properties?.name?.trim() ||
    `${latitude}, ${longitude}`;

  return {
    id:
      feature?.properties?.id?.trim() ||
      `${longitude.toString()},${latitude.toString()}`,
    label,
    provider: "digimaps",
    requiresResolve: false,
    latitude,
    longitude,
  };
}

export async function GetAddressList(token: string): Promise<ApiResponse> {
  return await request("/address/currentUser", {
    method: "GET",
    prefix: environment.baseUrl,
    requestType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  })
    .then(function (response: ApiResponse) {
      return response;
    })
    .catch(function (errorResponse) {
      return normalizeApiError(errorResponse);
    });
}

export async function GetAddress(
  token: string,
  id: string,
): Promise<ApiResponse> {
  return await request("/address/", {
    method: "GET",
    prefix: environment.baseUrl,
    suffix: id,
    requestType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  })
    .then(function (response: ApiResponse) {
      return response;
    })
    .catch(function (errorResponse) {
      return normalizeApiError(errorResponse);
    });
}

export async function CreateAddress(
  address: IAddress,
  token: string,
): Promise<ApiResponse> {
  return request("/address", {
    method: "POST",
    prefix: environment.baseUrl,
    body: JSON.stringify(address),
    requestType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  })
    .then(function (reponse: ApiResponse) {
      return reponse;
    })
    .catch(function (errorResponse) {
      return normalizeApiError(errorResponse);
    });
}

export async function EditAddress(
  addressRequest: IAddress,
  token: string,
): Promise<ApiResponse> {
  return request(`/address/${addressRequest.id}`, {
    method: "PUT",
    prefix: environment.baseUrl,
    body: JSON.stringify(addressRequest),
    requestType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  })
    .then(function (reponse: ApiResponse) {
      return reponse;
    })
    .catch(function (errorResponse) {
      return normalizeApiError(errorResponse);
    });
}

export async function DeleteAddress(
  token: string,
  id: string,
): Promise<ApiResponse> {
  return await request("/address/", {
    method: "DELETE",
    prefix: environment.baseUrl,
    suffix: id,
    requestType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  })
    .then(function (response: ApiResponse) {
      return response;
    })
    .catch(function (errorResponse) {
      return normalizeApiError(errorResponse);
    });
}

export async function GetMapConfig(
  token: string,
): Promise<ApiResponse<MapRuntimeConfig>> {
  return await request("/maps/config", {
    method: "GET",
    prefix: environment.baseUrl,
    requestType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  })
    .then(function (response: ApiResponse<MapRuntimeConfig>) {
      return response;
    })
    .catch(function (errorResponse) {
      return normalizeApiError<MapRuntimeConfig>(errorResponse);
    });
}

export async function SearchMapAddress(
  token: string,
  address: string,
  sessionToken?: string,
): Promise<ApiResponse<MapSuggestion[]>> {
  return await request("/maps/search", {
    method: "GET",
    prefix: environment.baseUrl,
    params: {
      query: address,
      sessionToken,
    },
    requestType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  })
    .then(function (response: ApiResponse<MapSuggestion[]>) {
      return response;
    })
    .catch(function (errorResponse) {
      return normalizeApiError<MapSuggestion[]>(errorResponse);
    });
}

export async function ResolveMapAddress(
  token: string,
  payload: ResolveMapAddressRequest,
): Promise<ApiResponse<ResolvedMapAddress>> {
  return await request("/maps/resolve", {
    method: "POST",
    prefix: environment.baseUrl,
    body: JSON.stringify(payload),
    requestType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  })
    .then(function (response: ApiResponse<ResolvedMapAddress>) {
      return response;
    })
    .catch(function (errorResponse) {
      return normalizeApiError<ResolvedMapAddress>(errorResponse);
    });
}

export async function SearchLegacyDigiMapAddress(
  token: string,
  address: string,
): Promise<ApiResponse<MapSuggestion[]>> {
  return await request("/address/searchDigiMapAddress/", {
    method: "GET",
    prefix: environment.baseUrl,
    suffix: address,
    requestType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  })
    .then(function (response: ApiResponse<LegacyDigiMapFeature[]>) {
      const nextData = Array.isArray(response.data)
        ? response.data
            .map((feature) => mapLegacySuggestion(feature))
            .filter((feature): feature is MapSuggestion => feature != null)
        : [];

      return {
        ...response,
        data: nextData,
      };
    })
    .catch(function (errorResponse) {
      return normalizeApiError<MapSuggestion[]>(errorResponse);
    });
}
